import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload } from '@fortawesome/pro-regular-svg-icons/faDownload'
import { faUpload } from '@fortawesome/pro-regular-svg-icons/faUpload'
import { faPlusCircle } from '@fortawesome/pro-regular-svg-icons/faPlusCircle'
import { faImage } from '@fortawesome/pro-regular-svg-icons/faImage'

import classNames from 'classnames'
import { Helmet } from 'react-helmet'
import {
    HydraTable,
    Heading,
    TextFilter,
    SelectFilter,
    DateRangeFilter,
    StyledCheckbox,
    TimeAgo,
} from '@peracto/peracto-ui'

import { useConfig } from '@peracto/peracto-config'
import { GET_LIST, useClient } from '@peracto/client'
import { useSettings } from '@peracto/peracto-hooks'
import startCase from 'lodash/startCase'
import {
    convertObjectToOptions,
    DEFAULT_PRODUCT_STATUS_VALUES,
    DEFAULT_PRODUCT_AVAILABILITY_VALUES,
} from '../util'

const ProductImage = ({ src, name }) => {
    return !!src ? (
        <img src={src} title={name} alt={name} style={{ height: '40px', width: '40px' }} />
    ) : (
        <FontAwesomeIcon
            icon={faImage}
            style={{ width: '40px', height: '40px', color: '#606060' }}
        />
    )
}

const ProductList = () => {
    // Default visible columns - key = column accessor
    const [selectedColumns, onSelectColumn] = useState({
        inventory: false,
        createdAt: false,
    })

    const { client } = useClient()
    const config = useConfig()
    const formatDate = config.get('user_format_date', config.get('format_date'))
    const { products } = config.get('features', {})
    const readOnly = products?.readOnly

    const { groups, values } = useSettings()

    const STATUS_OPTIONS =
        values?.productStatuses?.length > 0
            ? convertObjectToOptions(values.productStatuses)
            : DEFAULT_PRODUCT_STATUS_VALUES

    const AVAILABILITY_OPTIONS =
        values?.availability > 0
            ? convertObjectToOptions(values.availability)
            : DEFAULT_PRODUCT_AVAILABILITY_VALUES

    const TYPE_OPTIONS = convertObjectToOptions(groups?.type?.values?.types?.input?.options, true)

    // The 'selectable' property is used to define if the
    // column is available in the 'manage columns' dropdown
    const columns = useMemo(() => [
        {
            id: 'selection',
            Header: ({ getToggleAllRowsSelectedProps }) => (
                <StyledCheckbox {...getToggleAllRowsSelectedProps()} />
            ),
            Cell: ({ row }) => (
                <StyledCheckbox
                    {...row.getToggleRowSelectedProps()}
                    data-testid={`row-${row.index}-checkbox`}
                />
            ),
            selectable: false,
        },
        {
            accessor: 'primaryImage',
            Cell: row => {
                return (
                    <Link to={`/products/${row.row.original.originId}`}>
                        <ProductImage src={row.cell.value?.location} name={row.row.values.name} />
                    </Link>
                )
            },
            selectable: false,
        },
        {
            Header: 'Name',
            accessor: 'name',
            id: 'productName',
            sortType: 'basic',
            Filter: props => <TextFilter label="Name" {...props} />,
            Cell: ({ row, cell: { value } }) => (
                <Link
                    to={`/products/${row.original.originId}`}
                    data-testid={`row-${row.index}-name`}
                >
                    {value}
                </Link>
            ),
            selectable: false,
        },
        {
            Header: 'SKU',
            accessor: 'sku',
            sortType: 'basic',
            Filter: props => <TextFilter label="SKU" {...props} />,
            selectable: false,
        },
        {
            Header: 'Attribute Set',
            accessor: 'attributeSet.name',
            sortType: 'basic',
            Filter: props => <TextFilter label="Attribute Set" {...props} />,
            selectable: false,
        },
        {
            Header: 'Type',
            accessor: 'type',
            sortType: 'basic',
            Filter: props => <SelectFilter label="Type" options={TYPE_OPTIONS} {...props} />,
            Cell: ({ cell: { value } }) => {
                return startCase(value?.replaceAll('_', ' '))
            },
            selectable: false,
        },
        {
            Header: 'Status',
            accessor: 'status',
            sortType: 'basic',
            Filter: props => <SelectFilter label="Status" options={STATUS_OPTIONS} {...props} />,
            Cell: ({ cell: { value } }) => (
                <span
                    className={classNames('badge', 'badge-pill', {
                        'badge-info': value == 'draft',
                        'badge-success': value == 'active',
                        'badge-danger': value == 'deleted',
                    })}
                >
                    {startCase(value)}
                </span>
            ),
            selectable: false,
        },
        {
            Header: 'Last Modified',
            accessor: 'modifiedAt',
            sortType: 'basic',
            Filter: props => <DateRangeFilter label="Last Modified" {...props} />,
            Cell: ({ cell: { value } }) => {
                const date = new Date(value)
                const validDate = !!date.getDate() === true

                return value && validDate ? (
                    <TimeAgo title={formatDate(new Date(value))} date={value} />
                ) : (
                    '-'
                )
            },
            selectable: false,
        },
        {
            Header: 'Total Inventory',
            accessor: 'inventory',
            sortType: 'basic',
            selectable: true,
            show: selectedColumns['inventory'],
        },
        {
            Header: 'Created',
            accessor: 'createdAt',
            sortType: 'basic',
            Filter: props => <DateRangeFilter label="Created" {...props} />,
            Cell: ({ cell: { value } }) => {
                const date = new Date(value)
                const validDate = !!date.getDate() === true

                return value && validDate ? (
                    <TimeAgo title={formatDate(new Date(value))} date={value} />
                ) : (
                    '-'
                )
            },
            selectable: true,
            show: selectedColumns['createdAt'],
        },
    ])

    const availabilitySelect = {
        Header: 'Availability',
        accessor: 'availability',
        sortType: 'basic',
        Filter: props => (
            <SelectFilter label="Availability" options={AVAILABILITY_OPTIONS} {...props} />
        ),
        Cell: ({ cell: { value } }) => <span>{startCase(value)}</span>,
        selectable: false,
    }

    const withAvailability = [...columns, availabilitySelect]

    const onFetchData = options => client(GET_LIST, 'products', options)

    return (
        <>
            <Helmet>
                <title>Products | Peracto</title>
            </Helmet>
            <Heading name="Products">
                <div className="!ml-2 !flex !items-center">
                    {products?.exportProductData === true && (
                        <Link
                            className="!mr-2 btn btn-link"
                            to="/products/export"
                            data-testid="export-product-data"
                        >
                            <FontAwesomeIcon icon={faDownload} className="!mr-2" />
                            Export Product Data
                        </Link>
                    )}

                    <Link
                        className="!mr-2 btn btn-outline-primary"
                        to="/products/import"
                        data-testid="bulk-manage-data"
                    >
                        <FontAwesomeIcon icon={faUpload} className="!mr-2" />
                        Bulk Manage Data
                    </Link>

                    {!readOnly && (
                        <Link
                            className="btn btn-primary"
                            to="/products/add"
                            data-testid="add-product"
                        >
                            <FontAwesomeIcon icon={faPlusCircle} className="!mr-2" />
                            Add Product
                        </Link>
                    )}
                </div>
            </Heading>

            <div className="row">
                <div className="col">
                    <div className="card card--transparent">
                        <div className="card-body">
                            <HydraTable
                                columns={
                                    products?.availabilitySelectFilter ? withAvailability : columns
                                }
                                selectedColumns={selectedColumns}
                                onFetchData={onFetchData}
                                onSelectColumn={onSelectColumn}
                                defaultFilters={{
                                    productName: '',
                                    sku: '',
                                    'attributeSet.name': '',
                                    status: 'active',
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default ProductList
